import { Injectable } from '@angular/core';
import axios from 'axios';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenUrl = `${environment.apiBaseURL}/api/getToken`;
  private accessToken: string | null = null;
  private expiresAt: number | null = null;

  constructor() {
    const tokenData = JSON.parse(localStorage.getItem('authToken') || '{}');
    if (
      tokenData?.token &&
      tokenData?.expiresAt &&
      new Date().getTime() < tokenData.expiresAt
    ) {
      this.accessToken = tokenData.token;
      this.expiresAt = tokenData.expiresAt;
    }
  }

  /**
   * Get the access token. Will refresh the token if expired.
   */
  async getAccessToken(): Promise<string> {
    if (this.isTokenExpired()) {
      return this.refreshAccessToken();
    }

    // Return existing token if valid
    if (this.accessToken) {
      return this.accessToken;
    }

    // If no token, fetch a new one
    return this.fetchAccessToken();
  }

  /**
   * Check if the current token has expired.
   */
  private isTokenExpired(): boolean {
    if (!this.expiresAt) {
      return true; // Token hasn't been set or expiry time is missing
    }
    // Token is expired if current time exceeds expiry time
    return new Date().getTime() > this.expiresAt;
  }

  /**
   * Refresh the access token by fetching a new one from the API.
   */
  private async refreshAccessToken(): Promise<string> {
    try {
      return await this.fetchAccessToken();
    } catch (error) {
      console.error('Error refreshing access token:', error);
      throw error;
    }
  }

  /**
   * Fetch the access token from the authentication endpoint.
   */
  private async fetchAccessToken(): Promise<string> {
    try {
      const urlWithParams = `${this.tokenUrl}?clientId=${encodeURIComponent(environment.b2BAuthClientId)}`;
      const response = await axios.post(
        urlWithParams,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (response && response.data && response.data.AccessToken) {
        this.accessToken = response.data.AccessToken; // Store the token
        this.expiresAt = new Date().getTime() + 3600 * 1000; // Set expiration time (1 hour by default)

        if (!this.accessToken) {
          throw new Error('Access token is null or undefined');
        }

        localStorage.setItem(
          'authToken',
          JSON.stringify({
            token: this.accessToken,
            expiresAt: this.expiresAt,
          }),
        );

        return this.accessToken;
      } else {
        throw new Error('Invalid response structure from /getToken endpoint');
      }
    } catch (error: any) {
      console.error(
        'Error fetching access token:',
        error.response ? error.response.data : error,
      );
      throw new Error('Failed to retrieve access token');
    }
  }

  /**
   * Clear the stored access token and expiration time.
   */
  clearToken(): void {
    this.accessToken = null;
    this.expiresAt = null;
  }
}
